import { FC, ReactNode } from "react"
import { Check } from "lucide-react"

type FilterPillProps = {
  label: string
  count?: number
  isActive: boolean
  onClick: () => void
  icon?: ReactNode
  variant?: 'primary' | 'success' | 'neutral'
}

export const FilterPill: FC<FilterPillProps> = ({
  label,
  count,
  isActive,
  onClick,
  icon,
  variant = 'neutral'
}) => {
  const getVariantStyles = () => {
    if (!isActive) {
      return "bg-white/60 dark:bg-neutral800/60 text-neutral600 dark:text-neutral300 border-neutral200/50 dark:border-neutral700/50 hover:bg-white/80 dark:hover:bg-neutral800/80 hover:border-neutral300 dark:hover:border-neutral600"
    }

    switch (variant) {
      case 'primary':
        return "bg-sumoPrimary/90 text-sumoNavy border-sumoPrimary shadow-yellow dark:shadow-sumoPrimary/30"
      case 'success':
        return "bg-success500/90 text-white border-success600 shadow-lg shadow-success500/30"
      case 'neutral':
        return "bg-neutral700/90 text-white border-neutral800 shadow-lg shadow-neutral700/30"
      default:
        return ""
    }
  }

  return (
    <button
      onClick={onClick}
      className={`
        relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full
        border-2 backdrop-blur-md
        font-semibold text-sm
        transition-all duration-300 ease-out
        ${getVariantStyles()}
      `}
    >
      {/* Icon or Check Mark */}
      {isActive ? (
        <div className="flex items-center justify-center w-4 h-4 rounded-full bg-white/20 backdrop-blur-sm">
          <Check className="w-3 h-3" strokeWidth={3} />
        </div>
      ) : icon ? (
        <div className="flex items-center justify-center w-4 h-4">
          {icon}
        </div>
      ) : null}

      {/* Label */}
      <span className="relative z-10">{label}</span>

      {/* Count Badge */}
      {count !== undefined && (
        <span className={`
          inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold
          transition-all duration-200
          ${isActive
            ? "bg-white/30 dark:bg-black/20 backdrop-blur-sm"
            : "bg-neutral200/80 dark:bg-neutral700/80 text-neutral700 dark:text-neutral200"
          }
        `}>
          {count}
        </span>
      )}
    </button>
  )
}
